import { ErrorHandler } from '@angular/core';
import { PerformanceTracking } from '@freelancer/tracking';
import { fromPairs } from '@freelancer/utils';
import { sessionStorageVersions } from '../collection-versions';
import {
  DatastoreCollectionType,
  UserCollectionStateSlice,
} from '../store.model';
import {
  CollectionSliceCache,
  COLLECTION_CACHE_INTERFACE_VERSION,
  isCollectionSliceCache,
} from './local-cache.interface';

export function generateStorageKey(collectionType: string): string {
  return `datastore_local_cache_${collectionType}`;
}

/**
 * Get the cached collection state slice from the session storage
 * for the given collection type.
 */
export function getDatastoreCacheByCollectionType<
  C extends DatastoreCollectionType
>(
  collectionType: string,
  performanceTracking: PerformanceTracking,
  errorHandler: ErrorHandler,
): CollectionSliceCache<C> | undefined {
  let sessionStorageValue;
  const storageKey = generateStorageKey(collectionType);
  try {
    performanceTracking.mark('read-session-storage-start');
    sessionStorageValue = window.sessionStorage.getItem(storageKey);
    performanceTracking.mark('read-session-storage-end');
    performanceTracking.measure(
      'read-session-storage',
      'read-session-storage-start',
      'read-session-storage-end',
    );
  } catch (err) {
    // In case of an error, catch it and forward it to the error handler.
    errorHandler.handleError(err);
    return undefined;
  }

  // Parse the JSON string from session storage.
  const cachedCollectionState =
    (!!sessionStorageValue && JSON.parse(sessionStorageValue)) || undefined;

  return isCollectionSliceCache<C>(cachedCollectionState)
    ? cachedCollectionState
    : undefined;
}

/**
 * Store the given cache object into session storage by collection type.
 */
export function setDatastoreCacheByCollectionType<
  C extends DatastoreCollectionType
>(
  collectionType: string,
  cacheObj: CollectionSliceCache<C>,
  performanceTracking: PerformanceTracking,
): void {
  const storageKey = generateStorageKey(collectionType);
  try {
    performanceTracking.mark('write-session-storage-start');
    window.sessionStorage.setItem(storageKey, JSON.stringify(cacheObj));
    performanceTracking.mark('write-session-storage-end');
    performanceTracking.measure(
      'write-session-storage',
      'write-session-storage-start',
      'write-session-storage-end',
    );
  } catch (err) {
    // Ignore the errors, e.g. quota is full or security error.
  }
}

/**
 * Merge the given documents and queries into the current user collection state slice by:
 *   - adding missing documents and queries if it is not already exist
 *   - updating existing documents or queries if a newer version is available
 *
 * @param currentUserCollectionSlice The current user collection state slice object.
 * @param docsAndQueriesToAdd Documents and queries which we would like to merge into the current slice.
 */
export function mergeUserCollectionStateSlice<
  C extends DatastoreCollectionType
>(
  currentUserCollectionSlice: UserCollectionStateSlice<C> = {
    queries: {},
    documents: {},
  },
  docsAndQueriesToAdd: UserCollectionStateSlice<C> = {
    queries: {},
    documents: {},
  },
): UserCollectionStateSlice<C> {
  return {
    queries: {
      ...currentUserCollectionSlice.queries,
      ...fromPairs(
        Object.entries(docsAndQueriesToAdd.queries).filter(
          ([queryString, query]) => {
            const queryInCollection =
              currentUserCollectionSlice.queries[queryString];
            return (
              // Does not currently exist.
              !queryInCollection ||
              // This query result is older than the current query result.
              query.timeUpdated >= queryInCollection.timeUpdated
            );
          },
        ),
      ),
    },
    documents: {
      ...currentUserCollectionSlice.documents,
      ...fromPairs(
        Object.entries(docsAndQueriesToAdd.documents).filter(([id, doc]) => {
          const docInCollection = currentUserCollectionSlice.documents[id];
          return (
            // Does not currently exist.
            !docInCollection ||
            // This document is older than the one currently in the collection.
            doc.timeUpdated >= docInCollection.timeUpdated
          );
        }),
      ),
    },
  };
}

export function getJsonSchemaHash(collectionType: string): string {
  return sessionStorageVersions[collectionType];
}

export function getWebappBuildTimestamp(): number | undefined {
  return window?.webapp?.version?.buildTimestamp;
}

export function getCurrentCollectionCacheVersion(): number {
  return COLLECTION_CACHE_INTERFACE_VERSION;
}
